import { fetchQuery } from 'react-relay';

import {
	startCaptureGraphQlErrors,
	stopCaptureGraphQlErrors,
	type AGGError,
} from '@atlassian/relay-errors';

import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { isMeetingRecorderEligibilityEnabled } from '@confluence/loom-utils/entry-points/isMeetingRecorderEligibilityEnabled.ts';
import { createAtlAttributionHeader } from '@confluence/loom-utils/entry-points/createAtlAttributionHeader.ts';
import { PRELOADED_KEYS } from '@confluence/onboarding-helpers/entry-points/preloadViewPageOnboardingState.ts';
import {
	ONBOARDING_EXPERIENCES,
	OnboardingKeysCombined,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { ViewPagePersonalizationQuery } from './queries/ViewPagePersonalizationQuery.graphql.ts';
import type { ViewPagePersonalizationQuery as PersonalizationQuery } from './queries/__generated__/ViewPagePersonalizationQuery.graphql.ts';
import {
	buildViewPagePersonalizationVariables,
	type PersonalizationGroups,
} from './viewPagePersonalizationVariables.ts';

export type ViewPagePersonalizationResult =
	| {
			status: 'loaded';
			data: PersonalizationQuery['response'];
			variables: PersonalizationQuery['variables'];
			errors: ReadonlyArray<AGGError>;
	  }
	| { status: 'failed' }
	| undefined;

export async function preloadViewPagePersonalization({
	cloudId,
	activationId,
	contentId,
	spaceKey,
	isLoggedIn,
	accessStatus,
	isBlog,
	isAdminHubAIEnabled,
	groups,
}: {
	cloudId: string;
	activationId: string;
	contentId: string;
	spaceKey: string;
	isLoggedIn: boolean;
	accessStatus: AccessStatus;
	isBlog: boolean;
	isAdminHubAIEnabled: boolean;
	// Route-owned migration readiness, not rollout flags. Only opt in alongside a migrated consumer.
	groups: Partial<Record<keyof PersonalizationGroups, boolean>>;
}): Promise<ViewPagePersonalizationResult> {
	if (!window.GLOBAL_RELAY_ENV) return undefined;

	const included: PersonalizationGroups = {};
	if (
		groups.loom &&
		isLoggedIn &&
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_loom_reader')
	) {
		included.loom = { includeMeetingRecorderEligibility: false };
	}
	if (
		groups.recommendations &&
		isLoggedIn &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_recs_reader') &&
		!UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true)
	) {
		included.recommendations = { experience: '' };
	}
	if (
		groups.onboarding &&
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS) &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_onboarding_reader') &&
		expVal('confluence_pgxp_p2m_batch_1', 'isEnabled', false)
	) {
		included.onboarding = { keys: [] };
	}
	if (
		groups.omnibar &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_onboarding_reader') &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		fg('cc-ai-growth-preload-omnibar')
	) {
		included.omnibar = { keys: [ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN] };
	}
	if (
		groups.catchup &&
		isAdminHubAIEnabled &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_catchup_reader')
	) {
		included.catchup = true;
	}
	if (
		groups.presence &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_presence_reader')
	) {
		included.presence = true;
	}
	if (
		!Object.keys(included).length ||
		!expVal('confluence_exp_personalization_contract', 'isEnabled', false)
	) {
		return undefined;
	}

	if (included.loom) {
		included.loom.includeMeetingRecorderEligibility = isMeetingRecorderEligibilityEnabled();
	}
	if (included.recommendations) {
		included.recommendations.experience = expVal(
			'cc_ai_discovery_linked_pages_on_related_pages',
			'experience',
			'end_of_page_v5',
		);
	}
	if (included.onboarding) {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		included.onboarding.keys = fg('confluence_preload_all_onboarding_keys')
			? OnboardingKeysCombined
			: PRELOADED_KEYS;
	}
	const variables = buildViewPagePersonalizationVariables(
		{ cloudId, contentId, spaceKey, isBlog },
		included,
	);
	const collector = startCaptureGraphQlErrors();
	try {
		const data = await fetchQuery<PersonalizationQuery>(
			window.GLOBAL_RELAY_ENV,
			ViewPagePersonalizationQuery,
			variables,
			{
				fetchPolicy: 'store-or-network',
				networkCacheConfig: {
					metadata: { headers: createAtlAttributionHeader(cloudId, activationId) },
				},
			},
		).toPromise();
		const errors = stopCaptureGraphQlErrors(collector).flatMap(({ meta, errors }) =>
			meta.operationName === 'ViewPagePersonalizationQuery' ? errors : [],
		);
		// A field-path error belongs to that group. An unscoped failure cannot safely be assigned.
		if (!data || errors.some(({ path }) => !path?.length)) return { status: 'failed' };
		return { status: 'loaded', data, variables, errors };
	} catch {
		return { status: 'failed' };
	} finally {
		stopCaptureGraphQlErrors(collector);
	}
}
